import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────────────────────
   NfcMonitor.js — Live NFC reader debug dashboard
   • Connects to backend SSE stream of device tap events
   • Shows user info card for every tap (name, photo, flapid, plan, role)
   • Lets you do a manual UID / FlapID lookup
   • Lets you test the /api/device/tap endpoint directly
   • Shows raw JSON response for debugging
   Matches Home.js design system (navy / blue / gold)
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@600,700&display=swap');

:root {
  --navy:  #06112b; --navy2: #0d1f45; --navy3: #112359;
  --blue:  #1c73ba; --blue2: #2b8de0; --blue3: #5aaef7;
  --gold:  #f5a623; --gold2: #ffc94d;
  --green: #34d399; --red: #f87171; --amber: #fbbf24; --purple: #818cf8;
  --muted: #94a3b8; --border: rgba(28,115,186,0.18);
}

* { box-sizing: border-box; margin: 0; padding: 0; }

.mon-page {
  min-height: 100vh;
  background: linear-gradient(145deg, var(--navy) 0%, var(--navy3) 60%, #1a3570 100%);
  font-family: 'DM Sans', sans-serif; color: #fff;
}
.mon-page::before {
  content: ''; position: fixed; inset: 0;
  background-image:
    linear-gradient(rgba(28,115,186,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(28,115,186,0.04) 1px, transparent 1px);
  background-size: 52px 52px; pointer-events: none; z-index: 0;
}

/* NAV */
.mon-nav {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 24px; height: 64px;
  background: rgba(6,17,43,0.95); backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
}
.mon-logo { font-family: 'Clash Display', sans-serif; font-size: 1.4rem; font-weight: 700; color: #fff; }
.mon-logo span { color: var(--gold); }
.mon-status {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px;
}
.mon-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.mon-dot.live    { background: var(--green); animation: monPulse 2s infinite; }
.mon-dot.waiting { background: var(--amber); animation: monPulse 2s infinite; }
.mon-dot.offline { background: var(--red); }
@keyframes monPulse { 0%,100%{box-shadow:0 0 0 0 rgba(52,211,153,0.4);} 50%{box-shadow:0 0 0 5px rgba(52,211,153,0);} }

/* LAYOUT */
.mon-body {
  display: grid; grid-template-columns: 1fr 400px;
  gap: 20px; padding: 24px; max-width: 1300px; margin: 0 auto;
  position: relative; z-index: 1;
}
@media (max-width: 900px) { .mon-body { grid-template-columns: 1fr; } }

/* PANEL */
.mon-panel {
  background: rgba(13,31,69,0.7);
  border: 1px solid var(--border);
  border-radius: 18px; padding: 20px;
  backdrop-filter: blur(16px);
}
.mon-panel-title {
  font-family: 'Clash Display', sans-serif;
  font-size: 0.78rem; font-weight: 700; color: var(--blue3);
  text-transform: uppercase; letter-spacing: 2px; margin-bottom: 16px;
  display: flex; align-items: center; gap: 8px;
}

/* BIG USER CARD */
.mon-user-card {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(28,115,186,0.25);
  border-radius: 16px; padding: 22px;
  display: flex; gap: 18px; align-items: center;
  margin-bottom: 16px;
  animation: slideIn 0.35s ease;
  transition: border-color 0.3s;
}
.mon-user-card.ok     { border-color: rgba(52,211,153,0.35); }
.mon-user-card.lost   { border-color: rgba(248,113,113,0.35); }
.mon-user-card.unknown{ border-color: rgba(148,163,184,0.25); }
@keyframes slideIn { from { opacity:0; transform:translateY(-8px); } }

.mon-user-avatar {
  width: 72px; height: 72px; border-radius: 50%;
  object-fit: cover; flex-shrink: 0;
  border: 2px solid rgba(28,115,186,0.4);
}
.mon-user-initials {
  width: 72px; height: 72px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Clash Display', sans-serif;
  font-size: 26px; font-weight: 700; color: #fff; flex-shrink: 0;
  background: linear-gradient(135deg, var(--blue), var(--blue3));
}
.mon-user-name {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 4px;
}
.mon-user-meta { font-size: 0.8rem; color: var(--muted); margin-bottom: 8px; }
.mon-tag {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.7rem; font-weight: 700; padding: 3px 10px;
  border-radius: 99px; text-transform: uppercase; letter-spacing: 1px;
  margin-right: 5px;
}
.mon-tag.ok      { background: rgba(52,211,153,0.15);  color: var(--green);  border: 1px solid rgba(52,211,153,0.3); }
.mon-tag.lost    { background: rgba(248,113,113,0.15); color: var(--red);    border: 1px solid rgba(248,113,113,0.3); }
.mon-tag.unknown { background: rgba(148,163,184,0.1);  color: var(--muted);  border: 1px solid rgba(148,163,184,0.2); }
.mon-tag.staff   { background: rgba(129,140,248,0.15); color: var(--purple); border: 1px solid rgba(129,140,248,0.3); }
.mon-tag.loyalty { background: rgba(245,166,35,0.15);  color: var(--gold);   border: 1px solid rgba(245,166,35,0.3); }

/* OLED PREVIEW */
.mon-oled {
  background: #0a0a0a; border-radius: 10px; padding: 14px 16px;
  border: 2px solid #222; margin-top: 12px;
  font-family: 'Courier New', monospace; font-size: 13px; color: #33ff33;
  line-height: 1.6;
}
.mon-oled-line { display: block; }
.mon-oled-line.invert {
  background: #33ff33; color: #000; padding: 0 2px; border-radius: 2px;
}

/* FEED */
.mon-feed { display: flex; flex-direction: column; gap: 8px; max-height: 480px; overflow-y: auto; }
.mon-feed-item {
  display: flex; gap: 12px; align-items: center;
  padding: 10px 14px; border-radius: 10px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.05);
  cursor: pointer; transition: background 0.15s;
}
.mon-feed-item:hover { background: rgba(28,115,186,0.1); }
.mon-feed-item.selected { border-color: var(--blue3); background: rgba(28,115,186,0.1); }
.mon-feed-uid { font-family: monospace; font-size: 11px; color: var(--blue3); flex-shrink: 0; }
.mon-feed-name { font-size: 12px; font-weight: 600; color: #fff; }
.mon-feed-ts   { font-size: 10px; color: var(--muted); margin-left: auto; flex-shrink: 0; }
.mon-feed-status { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 99px; }
.mon-feed-status.ok      { background: rgba(52,211,153,0.15); color: var(--green); }
.mon-feed-status.lost    { background: rgba(248,113,113,0.15); color: var(--red); }
.mon-feed-status.unknown { background: rgba(148,163,184,0.1); color: var(--muted); }
.mon-feed-status.error   { background: rgba(248,113,113,0.15); color: var(--red); }

/* FORM */
.mon-input {
  width: 100%; padding: 10px 14px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(28,115,186,0.2);
  border-radius: 10px; color: #fff;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; outline: none;
  margin-bottom: 10px; transition: border 0.2s;
}
.mon-input:focus { border-color: var(--blue2); }
.mon-btn {
  width: 100%; padding: 11px;
  background: linear-gradient(135deg, var(--blue), var(--blue2));
  color: #fff; border: none; border-radius: 10px;
  font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 700;
  cursor: pointer; transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 4px 16px rgba(28,115,186,0.35);
  margin-bottom: 8px;
}
.mon-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(28,115,186,0.45); }
.mon-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.mon-btn-sec {
  width: 100%; padding: 10px;
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.7); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px; font-family: 'DM Sans', sans-serif;
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s; margin-bottom: 8px;
}
.mon-btn-sec:hover { background: rgba(255,255,255,0.1); color: #fff; }

/* RAW JSON */
.mon-raw {
  background: rgba(0,0,0,0.4); border-radius: 10px; padding: 14px;
  font-family: monospace; font-size: 11px; color: #6ee7b7;
  max-height: 200px; overflow-y: auto; margin-top: 12px;
  border: 1px solid rgba(52,211,153,0.12);
  white-space: pre-wrap; word-break: break-all;
}

/* STATS ROW */
.mon-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-bottom: 16px; }
.mon-stat { background: rgba(255,255,255,0.03); border: 1px solid var(--border);
  border-radius: 12px; padding: 12px; text-align: center; }
.mon-stat-num { font-family: 'Clash Display', sans-serif; font-size: 1.5rem; font-weight: 700; color: var(--blue3); }
.mon-stat-lbl { font-size: 0.7rem; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }

::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(28,115,186,0.3); border-radius: 99px; }
`;

const STATUS_LABELS = { ok:'✅ Found', lost:'🔴 Lost', unknown:'❓ Unknown', error:'⚠️ Error', defect:'⚠️ Defective' };

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
}

function UserCard({ tap }) {
  if (!tap) return (
    <div style={{ textAlign:'center', padding:'40px 0', color:'rgba(255,255,255,0.25)' }}>
      <div style={{ fontSize:40 }}>📡</div>
      <div style={{ marginTop:10, fontSize:13 }}>Waiting for first tap…</div>
    </div>
  );

  const u = tap.user;
  const l = tap.loyalty;
  const status = tap.status || 'unknown';

  return (
    <div>
      <div className={`mon-user-card ${status}`}>
        {u?.photo
          ? <img src={u.photo} alt="" className="mon-user-avatar"/>
          : <div className="mon-user-initials"
              style={{ background: status==='ok' ? 'linear-gradient(135deg,#1c73ba,#5aaef7)'
                                                  : 'linear-gradient(135deg,#334155,#475569)' }}>
              {(u?.name || '?')[0].toUpperCase()}
            </div>
        }
        <div style={{ flex:1, minWidth:0 }}>
          <div className="mon-user-name">{u?.name || tap.message || 'Unknown Card'}</div>
          <div className="mon-user-meta">
            {u?.flapid && <span>🪪 {u.flapid}</span>}
            {tap.uid && <span style={{marginLeft:8}}>🔖 {tap.uid}</span>}
          </div>
          <div style={{ marginTop:6 }}>
            <span className={`mon-tag ${status}`}>{STATUS_LABELS[status]||status}</span>
            {u?.plan && <span className="mon-tag ok">{u.plan}</span>}
            {u?.memberRole && u.memberRole !== 'member' && <span className="mon-tag staff">{u.memberRole}</span>}
            {l?.points !== undefined && <span className="mon-tag loyalty">⭐ {l.points} pts</span>}
          </div>
        </div>
        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)', marginBottom:4 }}>
            {fmtTime(tap.ts)}
          </div>
        </div>
      </div>

      {/* OLED preview */}
      {tap.display && (
        <div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)', textTransform:'uppercase',
            letterSpacing:1.5, marginBottom:6 }}>OLED Display Preview</div>
          <div className="mon-oled">
            <span className="mon-oled-line">{tap.display.line1 || ''}</span>
            <span className="mon-oled-line">{tap.display.line2 || ''}</span>
            <span className={`mon-oled-line ${u?.memberRole && u.memberRole !== 'member' ? 'invert' : ''}`}>
              {tap.display.line3 || ''}
            </span>
          </div>
        </div>
      )}

      {/* Loyalty details */}
      {l && status === 'ok' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginTop:12 }}>
          {[
            ['⭐', 'Points',   l.points ?? '—'],
            ['🔥', 'Streak',   l.streak  ?? '—'],
            ['👣', 'Visits',   l.totalVisits ?? '—'],
          ].map(([icon, lbl, val]) => (
            <div key={lbl} style={{ background:'rgba(255,255,255,0.03)', borderRadius:10,
              padding:'10px 8px', textAlign:'center', border:'1px solid var(--border)' }}>
              <div style={{ fontSize:16 }}>{icon}</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#fff',
                fontFamily:"'Clash Display',sans-serif" }}>{val}</div>
              <div style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1 }}>{lbl}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NfcMonitor() {
  const navigate  = useNavigate();
  const [taps,    setTaps]     = useState([]);      // feed history
  const [selected,setSelected] = useState(null);    // selected tap for detail
  const [connStatus, setConnStatus] = useState('waiting'); // live/waiting/offline
  const [lookupUid,  setLookupUid]  = useState('');
  const [lookupBiz,  setLookupBiz]  = useState('');
  const [apiKey,     setApiKey]     = useState('test-key');
  const [apiBase,    setApiBase]    = useState(() =>
    localStorage.getItem('flap_api_base') || 'http://localhost:5000');
  const [loading,    setLoading]    = useState(false);
  const [rawJson,    setRawJson]    = useState('');
  const [testUid,    setTestUid]    = useState('');
  const eventRef = useRef(null);
  const pingRef  = useRef(null);
  const countRef = useRef({ ok:0, lost:0, unknown:0 });

  // Save apiBase to localStorage
  useEffect(() => { localStorage.setItem('flap_api_base', apiBase); }, [apiBase]);

  // SSE connection to live analytics stream
  useEffect(() => {
    const connect = () => {
      try {
        const sse = new EventSource(`${apiBase}/api/analytics/live-stream`);
        sse.onopen    = () => setConnStatus('live');
        sse.onmessage = (e) => {
          try {
            const ev = JSON.parse(e.data);
            if (ev.type === 'heartbeat') return;
            // wrap analytics event as a tap display
            const tap = {
              id:      ev.id || String(Math.random()),
              ts:      Date.now(),
              status:  'ok',
              uid:     ev.uid || ev.accessoryFlapid || '',
              message: ev.message || 'Activity',
              user:    { name: ev.name || 'User', flapid: ev.flapid, photo: ev.photo },
              display: { line1: ev.name || 'Activity', line2: ev.type, line3: ev.city || '' },
              raw:     ev,
            };
            addTap(tap);
          } catch {}
        };
        sse.onerror = () => { sse.close(); setConnStatus('waiting'); };
        eventRef.current = sse;
      } catch { setConnStatus('offline'); }
    };
    connect();
    // Reconnect every 8s if offline
    pingRef.current = setInterval(() => {
      if (connStatus === 'waiting') connect();
    }, 8000);
    return () => {
      eventRef.current?.close();
      clearInterval(pingRef.current);
    };
  }, [apiBase]); // eslint-disable-line

  const addTap = useCallback((tap) => {
    setTaps(p => [tap, ...p].slice(0, 50));
    setSelected(tap);
    const s = tap.status || 'ok';
    countRef.current[s] = (countRef.current[s] || 0) + 1;
  }, []);

  // Manual lookup
  const doLookup = async () => {
    if (!lookupUid && !testUid) return;
    const uid = lookupUid || testUid;
    setLoading(true);
    try {
      const url = `${apiBase}/api/device/lookup?uid=${encodeURIComponent(uid)}&api_key=${encodeURIComponent(apiKey)}`
        + (lookupBiz ? `&business_id=${lookupBiz}` : '');
      const r = await fetch(url);
      const data = await r.json();
      setRawJson(JSON.stringify(data, null, 2));
      const tap = {
        id: String(Math.random()), ts: Date.now(),
        status:  data.status || 'unknown',
        uid,
        message: data.message || '',
        user:    data.user,
        loyalty: data.loyalty,
        display: data.display,
        raw:     data,
      };
      addTap(tap);
    } catch (err) {
      setRawJson(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  // Simulate a tap
  const doTestTap = async () => {
    if (!testUid) return;
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/api/device/tap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id:   'browser-test',
          api_key:      apiKey,
          tag_uid:      testUid,
          business_id:  lookupBiz || undefined,
        }),
      });
      const data = await r.json();
      setRawJson(JSON.stringify(data, null, 2));
      const tap = {
        id:      String(Math.random()), ts: Date.now(),
        status:  data.status || 'unknown',
        uid:     testUid,
        message: data.message || '',
        user:    data.user,
        loyalty: data.loyalty,
        display: data.display,
        raw:     data,
      };
      addTap(tap);
    } catch (err) {
      setRawJson(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  const stats = { ok: 0, lost: 0, unknown: 0 };
  taps.forEach(t => { stats[t.status] = (stats[t.status] || 0) + 1; });

  return (
    <div className="mon-page">
      <style>{STYLES}</style>

      {/* Nav */}
      <nav className="mon-nav">
        <div className="mon-logo">flap<span>.</span> NFC Monitor</div>
        <div className="mon-status">
          <span className={`mon-dot ${connStatus}`}/>
          <span style={{ color: connStatus==='live' ? 'var(--green)' : connStatus==='waiting' ? 'var(--amber)' : 'var(--red)' }}>
            {connStatus === 'live' ? 'Live' : connStatus === 'waiting' ? 'Connecting…' : 'Offline'}
          </span>
        </div>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'transparent', border:'1px solid var(--border)',
            borderRadius:8, padding:'6px 14px', color:'var(--muted)',
            cursor:'pointer', fontSize:13 }}>
          ← Dashboard
        </button>
      </nav>

      <div className="mon-body">

        {/* LEFT: main detail */}
        <div>
          <div className="mon-stats">
            <div className="mon-stat">
              <div className="mon-stat-num" style={{ color:'var(--green)' }}>{stats.ok || 0}</div>
              <div className="mon-stat-lbl">Found</div>
            </div>
            <div className="mon-stat">
              <div className="mon-stat-num" style={{ color:'var(--red)' }}>{stats.lost || 0}</div>
              <div className="mon-stat-lbl">Lost</div>
            </div>
            <div className="mon-stat">
              <div className="mon-stat-num" style={{ color:'var(--muted)' }}>{stats.unknown || 0}</div>
              <div className="mon-stat-lbl">Unknown</div>
            </div>
          </div>

          <div className="mon-panel">
            <div className="mon-panel-title">📡 Latest Tap</div>
            <UserCard tap={selected}/>
            {rawJson && (
              <details style={{ marginTop:14 }}>
                <summary style={{ fontSize:11, color:'var(--muted)', cursor:'pointer',
                  textTransform:'uppercase', letterSpacing:1.5 }}>Raw API Response</summary>
                <pre className="mon-raw">{rawJson}</pre>
              </details>
            )}
          </div>
        </div>

        {/* RIGHT: controls + feed */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Connection settings */}
          <div className="mon-panel">
            <div className="mon-panel-title">⚙️ Connection</div>
            <label style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, display:'block', marginBottom:5 }}>API Base URL</label>
            <input className="mon-input" value={apiBase} onChange={e=>setApiBase(e.target.value)}
              placeholder="http://192.168.1.100:5000"/>
            <label style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, display:'block', marginBottom:5 }}>API Key</label>
            <input className="mon-input" value={apiKey} onChange={e=>setApiKey(e.target.value)}
              placeholder="your-api-key"/>
            <label style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, display:'block', marginBottom:5 }}>Business ID (optional)</label>
            <input className="mon-input" value={lookupBiz} onChange={e=>setLookupBiz(e.target.value)}
              placeholder="leave blank for standalone"/>
            <button className="mon-btn" onClick={async () => {
              try {
                const r = await fetch(`${apiBase}/api/device/ping`);
                const d = await r.json();
                setRawJson(JSON.stringify(d, null, 2));
                setConnStatus('live');
              } catch (e) { setRawJson(`Ping failed: ${e.message}`); setConnStatus('offline'); }
            }}>🏓 Ping Server</button>
          </div>

          {/* Manual test */}
          <div className="mon-panel">
            <div className="mon-panel-title">🔬 Test Tap / Lookup</div>
            <label style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, display:'block', marginBottom:5 }}>UID or FlapID</label>
            <input className="mon-input" value={testUid} onChange={e=>setTestUid(e.target.value)}
              placeholder="e.g. 04:A1:B2:C3 or myusername"
              onKeyDown={e=>{ if(e.key==='Enter') doTestTap(); }}/>
            <button className="mon-btn" disabled={loading || !testUid} onClick={doTestTap}>
              {loading ? 'Sending…' : '⚡ Send Test Tap'}
            </button>
            <button className="mon-btn-sec" disabled={loading} onClick={doLookup}>
              🔍 Lookup Only (no recording)
            </button>
          </div>

          {/* Feed */}
          <div className="mon-panel" style={{ flex:1 }}>
            <div className="mon-panel-title" style={{ justifyContent:'space-between' }}>
              📋 Tap History
              <button onClick={()=>setTaps([])}
                style={{ background:'transparent', border:'none', color:'var(--muted)',
                  cursor:'pointer', fontSize:11 }}>Clear</button>
            </div>
            {taps.length === 0
              ? <div style={{ color:'rgba(255,255,255,0.2)', fontSize:12, textAlign:'center', padding:'20px 0' }}>
                  No taps yet — waiting for reader…
                </div>
              : <div className="mon-feed">
                  {taps.map(tap => (
                    <div key={tap.id} className={`mon-feed-item ${selected?.id===tap.id?'selected':''}`}
                      onClick={()=>{ setSelected(tap); setRawJson(JSON.stringify(tap.raw||tap, null, 2)); }}>
                      <span className="mon-feed-uid">{(tap.uid||'—').slice(0,11)}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="mon-feed-name"
                          style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {tap.user?.name || tap.message || 'Unknown'}
                        </div>
                      </div>
                      <span className={`mon-feed-status ${tap.status}`}>
                        {tap.status}
                      </span>
                      <span className="mon-feed-ts">{fmtTime(tap.ts)}</span>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}